// Data fetching with TanStack Query. Every page reads through these hooks, and every write
// invalidates what it changed, so pages stay consistent without reloading.
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useAuth } from '../auth/AuthProvider';
import { loadAppointments } from './appointments';
import { db } from './db';

export const qk = {
  appointments: (uid: string) => ['appointments', uid] as const,
  programs: ['programs'] as const,
  returns: ['returns'] as const,
  snapshots: ['snapshots'] as const,
  events: ['events'] as const,
  positions: ['positions'] as const,
  calls: ['calls'] as const,
  collectLog: ['collectLog'] as const,
  depot: ['depot'] as const,
  runs: ['runs'] as const,
  stockItems: ['stockItems'] as const,
  collectors: ['collectors'] as const,
  exchanges: ['exchanges'] as const,
  stars: ['stars'] as const,
  stopNotes: ['stopNotes'] as const,
  profiles: ['profiles'] as const,
};

/** All appointments (cached in this browser, only changes downloaded), with a progress count. */
export function useAppointments() {
  const { user } = useAuth();
  const uid = user?.id || '';
  const [progress, setProgress] = useState(0);
  const q = useQuery({
    queryKey: qk.appointments(uid),
    queryFn: () => loadAppointments(uid, setProgress),
    enabled: !!uid,
    refetchOnMount: 'always',
  });
  return { ...q, progress };
}

// Opening a page always re-reads what other planners may have changed meanwhile (the cached
// copy is shown until the fresh one arrives). Appointments only download what changed.
const fresh = { refetchOnMount: 'always' as const };
export const usePrograms = () => useQuery({ queryKey: qk.programs, queryFn: () => db.listPrograms(), ...fresh });
export const useReturns = () => useQuery({ queryKey: qk.returns, queryFn: () => db.listReturns(), ...fresh });
export const useSnapshots = () => useQuery({ queryKey: qk.snapshots, queryFn: () => db.listSnapshots(), ...fresh });
export const useEvents = () => useQuery({ queryKey: qk.events, queryFn: () => db.listEvents().catch(() => []), ...fresh });
export const usePositions = () => useQuery({ queryKey: qk.positions, queryFn: () => db.listPositions(), ...fresh });
// Until the calls table is added to the database (schema.sql), pages simply show no calls.
export const useCalls = () => useQuery({ queryKey: qk.calls, queryFn: () => db.listCalls().catch(() => []), ...fresh });
// Same for the stock tables: without them, the Stock page shows nothing yet.
export const useDepot = () => useQuery({ queryKey: qk.depot, queryFn: () => db.listDepot().catch(() => []), ...fresh });
export const useRuns = () => useQuery({ queryKey: qk.runs, queryFn: () => db.listRuns().catch(() => []), ...fresh });
export const useStockItems = () => useQuery({ queryKey: qk.stockItems, queryFn: () => db.listStockItems().catch(() => []), ...fresh });
export const useExchanges = () => useQuery({ queryKey: qk.exchanges, queryFn: () => db.listExchanges().catch(() => []), ...fresh });
export const useCollectors = () => useQuery({ queryKey: qk.collectors, queryFn: () => db.listCollectors().catch(() => []), ...fresh });
// Same for the collect_log table: without it, no tally of the day.
export const useCollectLog = () => useQuery({ queryKey: qk.collectLog, queryFn: () => db.listCollectLog().catch(() => []), ...fresh });
// Same for the starred_customers table: without it, only the automatic stars show.
export const useStars = () => useQuery({ queryKey: qk.stars, queryFn: () => db.listStars().catch(() => []), ...fresh });
// Same for the stop_notes table (update-stop-notes.sql): without it, no notes on the stops.
export const useStopNotes = () => useQuery({ queryKey: qk.stopNotes, queryFn: () => db.listStopNotes().catch(() => []), ...fresh });
export const useProfiles = () => useQuery({ queryKey: qk.profiles, queryFn: () => db.listProfiles() });

/** After an upload: appointments, snapshots and events have changed. */
export function useInvalidateAfterUpload() {
  const qc = useQueryClient();
  return () =>
    Promise.all([
      qc.invalidateQueries({ queryKey: ['appointments'] }),
      qc.invalidateQueries({ queryKey: qk.snapshots }),
      qc.invalidateQueries({ queryKey: qk.events }),
    ]);
}
